import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { applyDelta, bandFor, startingSentiment } from '@core/simulation/sentiment';
import { resolveOptions } from '@core/simulation/options';
import { audioFor, buyingSignalFor, customerLineFor, optionAudioFor, toneFor } from '@core/simulation/tone';
import { derivePersona, personaById } from '@core/simulation/persona';
import { resolveOutcome, outcomePasses } from '@core/simulation/outcomes';
import type { Outcome } from '@core/simulation/outcomes';
import { confusionSeverity } from '@core/scoring/drill';
import { stageKey } from '@core/shared/stages';
import { db } from '../firebase/config';
import { newSeed, shuffled } from './shuffle';
import type {
  BandName, OptionKey, OrgConfig, ScenarioDoc, ScenarioKey, ScenarioNode, Tier,
} from './types';

// §10.9 — `holding` is the beat after a close: the rep has answered and the
// prospect has not replied yet, which is the moment the rep has to learn not to
// fill.
export type SimPhase = 'loading' | 'choosing-persona' | 'playing' | 'holding' | 'ended' | 'error';

/**
 * §10.1 / §10.3 — classification comes before options, for both persona and
 * objection. Non-negotiable: if the options are on screen first the rep
 * reverse-engineers the answer from them and the measurement is worthless.
 */
export type SimStep = 'persona' | 'revision' | 'objection' | 'options';

export interface PersonaRead {
  selected: string;
  correct: boolean;
  severity: string | null;
  revisedFrom?: string;
}

export interface ObjectionRead {
  nodeId: string;
  selected: string;
  actual: string;
  correct: boolean;
}

export interface SimTurn {
  nodeId: string;
  line: string;
  optionId: string;
  optionText: string;
  /** §16.1 — the rep's own line in the rep voice, if one was rendered. */
  optionAudio: string | null;
  /** Null on a certification attempt: the client has no key to read it from. */
  tier: Tier | null;
  from: number;
  to: number;
  explain: { what: string; better: string };
  explainTapped: boolean;
  /** §6.10 — what kind of question the prospect asked, if they asked one. */
  buyingSignal?: string;
  compliance?: string;
  msElapsed: number;
}

/**
 * The simulation turn loop (§10.1).
 *
 * Practice mode, and entirely client-side: no function is called and no attempt
 * is written (§2, §18 Phase 3). A practice run is unlimited and fully revealing,
 * which is where the learning happens (§14.8). Submission and server scoring
 * arrive with the summative path.
 *
 * Persona is assigned for the session and modulates everything; classification
 * is not yet implemented, so the runner supplies the category a node actually
 * tests rather than one the rep chose. The resolver does not know the
 * difference — see `@core/simulation/options`.
 */
export interface SimulationArgs {
  orgId: string;
  scenarioId: string;
  config: OrgConfig;
  uid: string;
  salesRole: string | null;
  /**
   * §14.2 — the seed chosen at the certification gate, so the persona exclusion
   * decided there is the one the attempt actually runs and stores. Practice
   * makes its own: nothing is replayed, so nothing has to agree with it.
   */
  seed?: number;
}

export function useSimulation({
  orgId, scenarioId, config, uid, salesRole, seed: providedSeed,
}: SimulationArgs) {
  const [scenario, setScenario] = useState<ScenarioDoc | null>(null);
  const [key, setKey] = useState<ScenarioKey | null>(null);
  const [phase, setPhase] = useState<SimPhase>('loading');
  const [error, setError] = useState<string | null>(null);

  // The prospect is who they are regardless of how the rep reads them: the true
  // persona drives every meter movement (§5.3.6), while the rep's read decides
  // which option set they are served (§5.3.4).
  const [personaId, setPersonaId] = useState<string | null>(null);
  const [readPersona, setReadPersona] = useState<string | null>(null);
  const [revisedPersona, setRevisedPersona] = useState<string | null>(null);
  const [step, setStep] = useState<SimStep>('options');
  const [categories, setCategories] = useState<Record<string, string>>({});
  const [objectionReads, setObjectionReads] = useState<ObjectionRead[]>([]);
  const [closeSentiment, setCloseSentiment] = useState<number | null>(null);
  const [authoredOutcome, setAuthoredOutcome] = useState<string | null>(null);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [serverResult, setServerResult] = useState<Record<string, unknown> | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [nodeId, setNodeId] = useState<string | null>(null);
  /**
   * §10.9 — the beat after a close, where the rep should not be the first to
   * speak.
   *
   * A close is followed by silence, and the silence is the technique. Reps talk
   * through it because it is uncomfortable, and the discomfort is not something
   * a lesson can convey — it has to be sat through. So a node marked
   * `awaitsSilence` holds the prospect's reply back for a real interval before
   * revealing it, and the interval cannot be skipped: an escape hatch would be
   * used every time by exactly the people the beat exists for.
   *
   * The transition is computed at the moment of the answer and parked here, so
   * nothing about what the prospect says can depend on how the wait went.
   */
  const [pending, setPending] = useState<{ nextId: string | null; ending: boolean } | null>(null);
  const [sentiment, setSentiment] = useState(50);
  const [displayed, setDisplayed] = useState(50);   // lags, for the delayed reveal
  const [turns, setTurns] = useState<SimTurn[]>([]);
  const [previousTier, setPreviousTier] = useState<Tier | null>(null);

  const seed = useMemo(() => providedSeed ?? newSeed(), [scenarioId, providedSeed]);
  const shownAt = useRef(Date.now());
  const startedAt = useRef(new Date().toISOString());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sSnap = await getDoc(doc(db, `orgs/${orgId}/scenarios/${scenarioId}`));
        if (cancelled) return;
        if (!sSnap.exists()) throw new Error('missing scenario');
        setScenario(sSnap.data() as ScenarioDoc);

        // The answer key is readable only for practice content (§8.5, §9.3).
        // On a certification attempt the read is *supposed* to be refused, so it
        // is fetched separately and its denial is the expected outcome — not an
        // error, and never something that stops the call loading.
        try {
          const kSnap = await getDoc(doc(db, `orgs/${orgId}/scenarioKeys/${scenarioId}`));
          if (!cancelled && kSnap.exists()) setKey(kSnap.data() as ScenarioKey);
        } catch {
          if (!cancelled) setKey(null);
        }

        if (!cancelled) setPhase('choosing-persona');
      } catch (err) {
        console.error('could not load the scenario', err);
        if (!cancelled) { setError('That call could not be loaded.'); setPhase('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, scenarioId]);

  const persona = useMemo(
    () => personaById(config.personas, personaId),
    [config.personas, personaId]
  );

  /** §5.3.1 — in practice the rep may pick a persona or take a random one. */
  const start = useCallback((chosen: string | 'random') => {
    if (!scenario) return;
    // §5.5 — a summative attempt always runs with persona mode on and the
    // persona assigned from the seed, whatever the org's personaMode says and
    // whatever the rep would have picked.
    const id = chosen === 'random' || scenario.mode === 'summative'
      ? derivePersona(seed, scenario.eligiblePersonas)
      : chosen;
    const who = personaById(config.personas, id);
    if (!who) { setError('That prospect is not configured for this org.'); setPhase('error'); return; }

    const opening = startingSentiment(scenario.role, who, config.meter, scenario.inheritedBand);
    setPersonaId(id);
    setReadPersona(null);
    setRevisedPersona(null);
    setCategories({});
    setObjectionReads([]);
    setCloseSentiment(null);
    setAuthoredOutcome(null);
    setSentiment(opening);
    setDisplayed(opening);
    setNodeId(scenario.startNodeId);
    setStep(firstStep(scenario.nodes[scenario.startNodeId], scenario, null, null, {}));
    setPreviousTier(null);
    setTurns([]);
    setPhase('playing');
    shownAt.current = Date.now();
  }, [scenario, seed, config]);

  // §5.5 — a certification attempt assigns the persona and begins. The rep is
  // never offered the choice, so there is no screen to render in between.
  useEffect(() => {
    if (phase === 'choosing-persona' && scenario?.mode === 'summative') start('random');
  }, [phase, scenario, start]);

  const node: ScenarioNode | null = nodeId ? scenario?.nodes[nodeId] ?? null : null;
  const keyNode = nodeId ? key?.nodes[nodeId] : undefined;
  const tone = toneFor(previousTier);

  const line = node && personaId ? customerLineFor(node, personaId, tone) : '';
  const audio = node && personaId ? audioFor(node, personaId, tone) : null;

  /** The rep's current read: their revision if they made one, else their first call. */
  const servedPersona = revisedPersona ?? readPersona;

  const served = useMemo(() => {
    if (!node) return null;
    // §5.3.4 / §11.2 — the set served is the one for what the rep *named*, not
    // for what is true. Misread the prospect and you are handed the right moves
    // for somebody else; misname the objection and you handle one nobody raised.
    return resolveOptions(node, {
      selectedCategory: categories[node.id] ?? null,
      selectedPersona: servedPersona,
    });
  }, [node, categories, servedPersona]);

  /**
   * §5.3.4 — how badly a misread costs, scaled by how wrong it was (§5.6). Only
   * where a persona set was actually served: on a node with no persona variants
   * the rep was not running anybody's playbook in particular.
   */
  const mismatchPenalty = useMemo(() => {
    if (!served?.key.startsWith('persona:') || !personaId) return undefined;
    const servedFor = served.key.slice('persona:'.length);
    if (servedFor === personaId) return undefined;
    const severity = confusionSeverity(config, personaId, servedFor);
    return config.personaSeverityPenalty[severity];
  }, [served, personaId, config]);

  /** §14.1 — option order is shuffled every attempt, from the stored seed. */
  const options = useMemo(
    () => (served ? shuffled(served.options, seed, node?.id ?? '') : []),
    [served, seed, node]
  );

  const select = useCallback((optionId: string) => {
    if (!node || !scenario || !persona || !served || phase !== 'playing') return;

    const optionKeys: OptionKey[] = keyNode?.optionSets?.[served.key] ?? [];
    const chosen = optionKeys.find((o) => o.id === optionId);
    const text = served.options.find((o) => o.id === optionId)?.text ?? '';

    // On a certification attempt there is no key on the client — the rep may not
    // read one (§9.3) — so there is nothing to look the option up in and nothing
    // to show them if there were. The choice is recorded and the call moves on;
    // the server scores it from the same choices afterwards.
    if (!chosen && key) {
      console.error('no key entry for the chosen option', { nodeId, optionId, set: served.key });
      setError('That answer could not be scored. The content pack may be out of date.');
      setPhase('error');
      return;
    }

    const movement = chosen ? applyDelta(sentiment, {
      delta: chosen.sentimentDelta,
      tier: chosen.tier,
      // The true persona, always: the prospect does not become someone else
      // because the rep misread them (§5.3.6).
      persona,
      meter: config.meter,
      category: keyNode?.actualCategory,
      compliance: !!chosen.compliance,
      mismatchPenalty,
    }) : { from: sentiment, to: sentiment, applied: 0, tier: null, bonusApplied: false, mismatched: false };

    setTurns((prev) => [...prev, {
      nodeId: node.id,
      line,
      optionId,
      optionText: text,
      optionAudio: optionAudioFor(node, served.key, optionId),
      tier: chosen?.tier ?? null,
      from: movement.from,
      to: movement.to,
      explain: chosen?.explain ?? { what: '', better: '' },
      explainTapped: false,
      // §6.10 — read off the line that rendered, which a persona override or a
      // tone variant may have replaced outright.
      buyingSignal: keyNode && persona ? buyingSignalFor(keyNode, node, persona.id, tone) ?? undefined : undefined,
      compliance: chosen?.compliance,
      msElapsed: Date.now() - shownAt.current,
    }]);

    setSentiment(movement.to);
    setPreviousTier(chosen?.tier ?? null);
    if (chosen?.outcome) setAuthoredOutcome(chosen.outcome);

    // §10.2 — every node has exactly one `next`. Consequence is carried by what
    // the prospect says next, never by a different path.
    const nextId = node.next ?? null;
    const nextNode = nextId ? scenario.nodes[nextId] : null;

    // §10.5 — the outcome is resolved against the band *at initiation*. Post-close
    // nodes still move the meter, but the transfer was already committed.
    if (nextNode?.isPostClose && closeSentiment === null) setCloseSentiment(movement.to);

    const ending = !nextNode || !!nextNode.isOutcome;
    if (ending && closeSentiment === null && !nextNode?.isPostClose) setCloseSentiment(movement.to);

    // §10.9 — hold before the prospect answers, where the node says to.
    if (node.awaitsSilence && (nextId || ending)) {
      setPending({ nextId, ending });
      setPhase('holding');
      return;
    }

    if (ending) {
      setNodeId(nextId);
      setPhase('ended');
    } else {
      setNodeId(nextId);
      setStep(firstStep(nextNode!, scenario, readPersona, revisedPersona, categories));
      shownAt.current = Date.now();
    }
  }, [node, scenario, persona, served, keyNode, key, sentiment, config, line, phase, nodeId,
      mismatchPenalty, closeSentiment, readPersona, revisedPersona, categories]);

  /**
   * §10.9 — the silence runs out and the prospect answers.
   *
   * A plain timer rather than anything the rep can dismiss. `holdMs` comes from
   * the org's pack so a floor that wants four seconds and one that wants seven
   * are a content change rather than a deploy.
   */
  const holdMs = config.simulation?.silenceHoldMs ?? 4000;
  useEffect(() => {
    if (phase !== 'holding' || !pending) return;
    const timer = setTimeout(() => {
      const next = pending.nextId ? scenario?.nodes[pending.nextId] : null;
      setNodeId(pending.nextId);
      if (pending.ending || !next) {
        setPhase('ended');
      } else {
        setStep(firstStep(next, scenario!, readPersona, revisedPersona, categories));
        shownAt.current = Date.now();
        setPhase('playing');
      }
      setPending(null);
    }, holdMs);
    return () => clearTimeout(timer);
  }, [phase, pending, holdMs, scenario, readPersona, revisedPersona, categories]);

  // §10.4 — delayed reveal. The meter animates as the customer's reply renders,
  // not on the click, so the feedback attaches to the prospect's reaction rather
  // than to a scoring machine.
  useEffect(() => {
    if (phase !== 'playing' && phase !== 'ended') return;
    const timer = setTimeout(() => setDisplayed(sentiment), 90);
    return () => clearTimeout(timer);
  }, [nodeId, sentiment, phase]);

  // §10.6 — log the tap. A rep who never opens the explanation after a bad
  // choice is not learning, and it surfaces in reporting.
  const tapExplain = useCallback((index: number) => {
    setTurns((prev) => prev.map((t, i) => (i === index ? { ...t, explainTapped: true } : t)));
  }, []);

  /** §5.3.3 — the rep names the prospect after two or three exchanges. */
  const classifyPersona = useCallback((selected: string) => {
    if (!scenario || !personaId) return;
    setReadPersona(selected);
    setStep(node?.requiresClassification && !categories[node.id] ? 'objection' : 'options');
    shownAt.current = Date.now();
  }, [scenario, personaId, node, categories]);

  /**
   * §5.3.5 — one revision is permitted. Revising *to* the correct persona earns
   * points; revising away from it costs. Real prospects do not announce
   * themselves and good reps update.
   */
  const revisePersona = useCallback((selected: string | null) => {
    if (selected && selected !== readPersona) setRevisedPersona(selected);
    setStep(node?.requiresClassification && node && !categories[node.id] ? 'objection' : 'options');
    shownAt.current = Date.now();
  }, [readPersona, node, categories]);

  /** §11.2 — mandatory, and presented before the response options. */
  const classifyObjection = useCallback((selected: string) => {
    if (!node) return;
    const actual = keyNode?.actualCategory ?? '';
    setCategories((prev) => ({ ...prev, [node.id]: selected }));
    setObjectionReads((prev) => [...prev, {
      nodeId: node.id, selected, actual, correct: selected === actual,
    }]);
    setStep('options');
    shownAt.current = Date.now();
  }, [node, keyNode]);

  const band: BandName = bandFor(displayed, config.meter.bands);

  /**
   * §8.6 — the client writes the choices it made and nothing else. It does not
   * write `assignedPersona` on a summative attempt: security rules refuse it,
   * and the server re-derives the persona from `shuffleSeed` anyway (§9.7).
   */
  useEffect(() => {
    if (phase !== 'ended' || scenario?.mode !== 'summative' || attemptId || submitting) return;
    setSubmitting(true);
    (async () => {
      try {
        const ref = await addDoc(collection(db, `orgs/${orgId}/attempts`), {
          uid, orgId,
          scenarioId,
          type: 'simulation',
          // §4.1 — the attempt is recorded against the role-scoped stage, so the
          // cap, the cooldown and the progress entry all belong to the track the
          // rep is certifying in rather than to both at once.
          stage: stageKey(scenario.stage, salesRole, config.roleScopedStages),
          salesRole,
          mode: 'summative',
          contentVersion: scenario.contentVersion,
          shuffleSeed: seed,
          personaSelected: readPersona,
          ...(revisedPersona ? { personaRevised: revisedPersona } : {}),
          startedAt: startedAt.current,
          submittedAt: new Date().toISOString(),
          turns: turns.map((t) => ({
            nodeId: t.nodeId,
            optionId: t.optionId,
            ...(categories[t.nodeId] ? { classificationSelected: categories[t.nodeId] } : {}),
            explainTapped: false,
            msElapsed: t.msElapsed,
          })),
        });
        setAttemptId(ref.id);
      } catch (err) {
        console.error('could not submit the attempt', err);
        setError('Your attempt could not be submitted. Nothing has been recorded — tell your manager before trying again.');
        setPhase('error');
      }
    })();
  }, [phase, scenario, attemptId, submitting, orgId, uid, scenarioId, salesRole, seed,
      readPersona, revisedPersona, turns, categories, config]);

  // The server scores it and appends `result` (§8.6). Everything a rep is shown
  // afterwards comes from that record, not from a key they cannot read.
  useEffect(() => {
    if (!attemptId) return;
    return onSnapshot(
      doc(db, `orgs/${orgId}/attempts/${attemptId}`),
      (snap) => {
        const scored = snap.data()?.result as Record<string, unknown> | undefined;
        if (scored) setServerResult(scored);
      },
      (err) => {
        console.error('could not read the scored attempt', err);
        setError('Your attempt was recorded, but the result could not be read back.');
      }
    );
  }, [attemptId, orgId]);

  /** How the rep read the prospect, and how wrong that was (§5.6). */
  const personaRead: PersonaRead | null = useMemo(() => {
    if (!readPersona || !personaId) return null;
    const finalRead = revisedPersona ?? readPersona;
    return {
      selected: finalRead,
      correct: finalRead === personaId,
      severity: finalRead === personaId ? null : confusionSeverity(config, personaId, finalRead),
      ...(revisedPersona ? { revisedFrom: readPersona } : {}),
    };
  }, [readPersona, revisedPersona, personaId, config]);

  // §10.5 — resolved once, against the band at initiation.
  const outcome: Outcome | null = useMemo(() => {
    if (phase !== 'ended' || !scenario) return null;
    const at = closeSentiment ?? sentiment;
    return resolveOutcome({
      role: scenario.role,
      band: bandFor(at, config.meter.bands),
      persona,
      authored: authoredOutcome,
      // §10.5 — the 5% path where the appointment is the close.
      scheduled: scenario.handoff === 'scheduled',
    });
  }, [phase, scenario, closeSentiment, sentiment, config, persona, authoredOutcome]);

  const closeBand: BandName = bandFor(closeSentiment ?? sentiment, config.meter.bands);
  const outcomePassed = outcome
    ? outcomePasses(outcome, closeBand, config.certification.minimumCloseBand as BandName)
    : null;

  /** The categories offered on this node (§11.3), or the whole taxonomy. */
  const objectionChoices = node?.classificationChoices ?? config.objectionTaxonomy;
  const personaChoices = node?.personaChoices ?? scenario?.eligiblePersonas ?? [];

  /**
   * §4.3 — what the setter said, as the closer picks up. It stays on screen for
   * the whole call: the closer is working against a claim somebody else made,
   * and half the job is noticing when that claim needs resetting.
   */
  const briefing = scenario?.briefing?.text ?? null;

  return {
    scenario, key, phase, step, error, persona, personaId, node, line, audio, options, briefing,
    // §10.9 — how long the silence lasts, so the screen can show it running out
    // rather than looking like it has stopped responding.
    holdMs,
    turns, sentiment, displayed, band, start, select, tapExplain,
    readPersona, revisedPersona, servedPersona, personaRead, objectionReads,
    objectionChoices, personaChoices, classifyPersona, revisePersona, classifyObjection,
    outcome, outcomePassed, closeBand, mismatched: mismatchPenalty !== undefined,
    // Explanations are practice-only mid-attempt; in summative they would turn
    // the meter into an answer key (§10.6, §14.4).
    // §10.6 / §14.4 — explanations and the meter are practice-only mid-attempt.
    // In summative they would turn the meter into an answer key, and there is no
    // key on the client to read from in any case.
    revealing: scenario?.mode === 'practice' && !!key,
    summative: scenario?.mode === 'summative',
    attemptId, serverResult, submitting, seed,
  };
}

/**
 * Which step a node opens on. Classification always precedes options (§10.3),
 * and the persona chooser precedes the objection chooser (§10.1). If the options
 * are on screen first the rep reverse-engineers the answer from them, and the
 * measurement is worthless.
 */
function firstStep(
  node: ScenarioNode | undefined,
  scenario: ScenarioDoc,
  read: string | null,
  revised: string | null,
  categories: Record<string, string>
): SimStep {
  if (!node) return 'options';
  if (node.requiresPersonaClassification && !read) return 'persona';
  if (node.id === scenario.personaRevisionNodeId && read && !revised) return 'revision';
  if (node.requiresClassification && !categories[node.id]) return 'objection';
  return 'options';
}
